import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function SetupAccount() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [verifying, setVerifying] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const init = async () => {
      // Supabase auto-processes recovery/invite hash and creates a session
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      const errorDescription = hashParams.get('error_description');

      if (errorDescription) {
        setErrorMsg(decodeURIComponent(errorDescription).replace(/\+/g, ' '));
        setVerifying(false);
        return;
      }

      // Wait briefly for supabase to set the session from the URL
      await new Promise((r) => setTimeout(r, 300));

      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user?.email) {
        setErrorMsg('This setup link is invalid or has expired. Please request a new invitation.');
        setVerifying(false);
        return;
      }

      if (type && type !== 'recovery' && type !== 'invite') {
        // unexpected, but allow if session exists
      }

      setEmail(session.user.email);
      setAuthorized(true);
      setVerifying(false);
    };
    init();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      setFormError(error.message || 'Failed to set up your account. Please try again.');
      setSubmitting(false);
      return;
    }

    // Mark invitation as accepted
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (userId) {
        await supabase
          .from('platform_admins')
          .update({ invitation_status: 'accepted', updated_at: new Date().toISOString() })
          .eq('id', userId);
        await supabase
          .from('organization_members')
          .update({ invitation_status: 'accepted', updated_at: new Date().toISOString() })
          .eq('id', userId);
      }
    } catch {
      // ignore
    }

    // Sign out so the user can log in fresh with their new credentials
    await supabase.auth.signOut();

    setSuccess(true);
    setSubmitting(false);
    toast({
      title: 'Account Created',
      description: 'Your account is now active. Please sign in with your new password.',
    });
    setTimeout(() => navigate('/admin/login'), 1800);
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <div className="flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-lg text-foreground">Verifying your invitation...</span>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invitation Unavailable</CardTitle>
            <CardDescription>{errorMsg}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/admin/login')} className="w-full">
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-6 w-6" />
              <CardTitle>Account Activated</CardTitle>
            </div>
            <CardDescription>
              Your account has been created. Redirecting you to the sign-in page...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Set Up Your Account</CardTitle>
          <CardDescription>
            Create a password to activate your Maturity Compass account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="setup-email">Email</Label>
              <Input id="setup-email" type="email" value={email} disabled readOnly />
              <p className="text-xs text-muted-foreground">
                Verified from your invitation. Only this email can complete setup.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="setup-password">Password</Label>
              <div className="relative">
                <Input
                  id="setup-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={submitting}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="setup-confirm">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="setup-confirm"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={submitting}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {submitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
